import React, { useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";

function makeKey(): string {
  const g: any = globalThis;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return "tl-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export type TaskShape = {
  id: string; title: string; task_type_code: string;
  status: "open" | "in_progress" | "completed" | "cancelled";
  version: number; due_today: boolean; overdue: boolean; upcoming: boolean;
  priority: string; all_day: boolean; due_at?: string | null;
  due_date?: string | null; assigned_to_user_id: string;
  contact_id?: string | null; organization_id?: string | null;
  opportunity_id?: string | null; notes?: string | null;
  completed_at?: string | null; cancelled_at?: string | null;
  cancelled_reason?: string | null;
  created_at?: string | null; updated_at?: string | null;
  created_by_name?: string | null; assigned_to_name?: string | null;
  updated_by_name?: string | null; completed_by_name?: string | null;
  cancelled_by_name?: string | null;
};

type LifecycleMode = "start" | "complete" | "cancel";
type Props = {
  task: TaskShape;
  onRefetch: () => void | Promise<void>;
  readOnly?: boolean;
};

export const TaskItem: React.FC<Props> = ({ task, onRefetch, readOnly }) => {
  const [dialog, setDialog] = useState<LifecycleMode | null>(null);
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const keyRef = useRef<string>(makeKey());
  const badge = useMemo(() => {
    if (task.status === "completed") return { text: "Completed", cls: "bg-green-50 text-green-700 border-green-200" };
    if (task.status === "cancelled") return { text: "Cancelled", cls: "bg-slate-100 text-slate-600 border-slate-200" };
    if (task.overdue) return { text: "Overdue", cls: "bg-red-50 text-red-700 border-red-200" };
    if (task.due_today) return { text: "Due today", cls: "bg-amber-50 text-amber-800 border-amber-200" };
    if (task.upcoming) return { text: "Upcoming", cls: "bg-sky-50 text-sky-700 border-sky-200" };
    return { text: task.status, cls: "bg-slate-50 text-slate-600 border-slate-200" };
  }, [task]);

  async function runAction(mode: LifecycleMode) {
    setSubmitting(true); setError("");
    try {
      const body: any = { if_version: task.version };
      if (mode === "cancel") body.reason = reason.trim();
      await api.post(`/crm/tasks/${task.id}/${mode}`, body, {
        headers: { "Idempotency-Key": keyRef.current },
      });
      keyRef.current = makeKey();
      setDialog(null); setReason("");
      onRefetch();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" && detail ? detail.code : detail;
      if (e?.response?.status === 409 && code === "version_conflict") {
        keyRef.current = makeKey();
        await onRefetch();
        setError("This task changed since the page loaded. We refreshed it—please review the latest status and try again.");
      } else {
        const friendly = code === "illegal_transition"
          ? "This task can no longer be changed from its current status. Refresh the list and review its latest state."
          : code === "invalid_task_event" || code === "unknown_action"
            ? "That task action is not supported. Refresh the page and try again."
            : formatApiError(detail);
        setError(friendly || "Could not update this task. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const dueLabel = task.all_day
    ? (task.due_date ?? "")
    : task.due_at ? new Date(task.due_at).toLocaleString() : "";

  return (
    <div className="border border-slate-200 rounded-md bg-white p-3 flex items-start justify-between gap-3"
         data-testid={`task-item-${task.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 truncate" data-testid="task-item-title">{task.title}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${badge.cls}`}
                data-testid={`task-item-badge-${badge.text.toLowerCase().replace(/ /g,"-")}`}>{badge.text}</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {task.task_type_code} · {task.priority} · {task.all_day ? "all day" : dueLabel}
        </div>
        <div className="mt-1 space-y-0.5 text-xs text-slate-500" data-testid={`task-attribution-${task.id}`}>
          <div>Created by {task.created_by_name || "Unknown user"}{task.created_at ? ` · ${new Date(task.created_at).toLocaleString()}` : ""}</div>
          <div>Assigned to {task.assigned_to_name || "Unknown user"}</div>
          {task.status === "completed" && <div>Completed by {task.completed_by_name || "Unknown user"}{task.completed_at ? ` · ${new Date(task.completed_at).toLocaleString()}` : ""}</div>}
          {task.status === "cancelled" && <div>Cancelled by {task.cancelled_by_name || "Unknown user"}{task.cancelled_at ? ` · ${new Date(task.cancelled_at).toLocaleString()}` : ""}</div>}
          {task.updated_at && task.updated_at !== task.created_at && <div>Last updated by {task.updated_by_name || "Unknown user"} · {new Date(task.updated_at).toLocaleString()}</div>}
        </div>
        {task.cancelled_reason ? (
          <div className="text-xs text-slate-500 mt-1">Cancelled: {task.cancelled_reason}</div>
        ) : null}
      </div>
      {!readOnly && task.status !== "completed" && task.status !== "cancelled" ? (
        <div className="flex items-center gap-1 flex-shrink-0">
          {task.status === "open" ? (
            <Button size="sm" variant="outline" onClick={() => runAction("start")}
              disabled={submitting}
              data-testid={`task-item-start-btn-${task.id}`}>Start</Button>
          ) : null}
          <Button size="sm" onClick={() => setDialog("complete")}
            disabled={submitting}
            data-testid={`task-item-complete-btn-${task.id}`}>Complete</Button>
          <Button size="sm" variant="ghost" onClick={() => setDialog("cancel")}
            disabled={submitting}
            data-testid={`task-item-cancel-btn-${task.id}`}>Cancel</Button>
        </div>
      ) : null}

      {/* Complete dialog */}
      <Dialog open={dialog === "complete"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="task-complete-dialog">
          <DialogHeader>
            <DialogTitle>Complete task</DialogTitle>
            <DialogDescription>Mark &quot;{task.title}&quot; as completed?</DialogDescription>
          </DialogHeader>
          {error ? <div role="alert" className="text-sm text-red-600" data-testid="task-complete-error">{error}</div> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}
              data-testid="task-complete-cancel-btn">Cancel</Button>
            <Button onClick={() => runAction("complete")} disabled={submitting}
              data-testid="task-complete-submit-btn">Complete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={dialog === "cancel"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="task-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel task</DialogTitle>
            <DialogDescription>Provide a reason (required).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`cancel-reason-${task.id}`}>Reason</Label>
            <Textarea id={`cancel-reason-${task.id}`} value={reason}
              onChange={(e) => setReason(e.target.value)} rows={3}
              data-testid="task-cancel-reason-input" />
            {error ? <div role="alert" className="text-sm text-red-600" data-testid="task-cancel-error">{error}</div> : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}
              data-testid="task-cancel-close-btn">Close</Button>
            <Button onClick={() => runAction("cancel")}
              disabled={submitting || reason.trim().length === 0}
              data-testid="task-cancel-submit-btn">Cancel task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const TaskList: React.FC<{
  tasks: TaskShape[]; onRefetch: () => void | Promise<void>; readOnly?: boolean;
  emptyText?: string; testId?: string;
}> = ({ tasks, onRefetch, readOnly, emptyText, testId }) => {
  if (!tasks || tasks.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-3"
        data-testid={testId ? `${testId}-empty` : "task-list-empty"}>
        {emptyText || "Nothing here yet."}
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={testId || "task-list"}>
      {tasks.map((t) => (
        <TaskItem key={t.id} task={t} onRefetch={onRefetch} readOnly={readOnly} />
      ))}
    </div>
  );
};
